import React, { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ArrowRight, Building2, Search } from 'lucide-react';
import { useApi } from '../../lib/api';
import { fetchJson } from '../../lib/apiFetchJson';
import {
  FlowSearchResponseSchema,
  type FlowSearchResponse,
  type FlowSearchScope,
  type AccountSearchResult,
  type ContactSearchResult,
} from '@shared/flow/search';
import { parseSearchIdentifier } from '@shared/flow/identifier';
import { useFlowLookup } from './useFlowLookup';
import { useDebouncedValue } from '../flow/useDebouncedValue';
import { useClickOutside } from '../flow/useClickOutside';
import { parseDomainLink } from '../flow/links';
import { getInitials, avatarColor } from '../../view-system/utils/displayFormatters';
import { AUTOFILL_IGNORE_PROPS } from '../../lib/autofill';

/** How long the query must sit still before the search request fires. */
export const GLOBAL_SEARCH_DEBOUNCE_MS = 250;

/**
 * Global search omnibox — a jump-to across accounts AND contacts (one query,
 * two sections, keyboard-navigable). Core-owned and host-agnostic: navigation
 * is injected via onSelectAccount/onSelectContact, so any host (Command
 * Center today; a global chrome later) supplies its own routes. Narrowing a
 * ranked list stays on the tables' Filters builder — this box navigates.
 *
 * Scope: 'my' book by default with a pinned "Search all records" escalation
 * that re-runs the same query across the whole mirror.
 */

const MIN_QUERY_LENGTH = 2;
const PREFERRED_SLOTS = 6;
const OTHER_SLOTS = 4;

export interface GlobalSearchProps {
  /** Active subtab entity — its section renders first with more slots (6 vs 4). */
  activeEntity: 'account' | 'contact';
  onSelectAccount: (accountId: string) => void;
  /**
   * `row` is the full search result the rep clicked — hosts that need more
   * than the bare id (e.g. a contact-card popover,
   * R6: header renders from this row even when a downstream detail read
   * 404s) can read name/email/title/account_name off it without a second
   * fetch. Existing hosts that only destructure the first two args are
   * unaffected (JS/TS callback contravariance).
   */
  onSelectContact?: (contactId: string, accountId: string | null, row: ContactSearchResult) => void;
  /**
   * 'contacts' hides account results entirely (embedded mounts that only act
   * on contacts, e.g. the enroll-queue sidebar) and sizes to its container
   * (w-full input + dropdown). 'accounts' is the mirror — hides contact
   * results entirely (the Flow dock's header account search-to-switch
   * popover, U5), also sizing to its container. Default 'all' — the Command
   * Center omnibox.
   */
  mode?: 'all' | 'contacts' | 'accounts';
  /**
   * R6 — "Recents in the dropdown on focus". When provided (non-empty) and
   * the query is empty, focusing the input opens a "Recent" section listing
   * these rows instead of the usual "type to search" prompt; no network
   * request backs this branch (the caller already has the rows). Omitted by
   * hosts with no recency source (e.g. Command Center's `mode="all"` mount)
   * — behavior there is unchanged.
   */
  recentContacts?: ContactSearchResult[];
  /**
   * Search Results surface (flow-sidebar-redesign-todos follow-on) — an
   * additive, opt-in escalation from the omnibox's ~5-row dropdown to a
   * full scrollable results surface the host renders itself. Omitted by
   * hosts with no such surface (Command Center's `mode="all"` mount) —
   * behavior there is byte-for-byte unchanged.
   *
   * Wired to two paths, both requiring `q.trim().length >= MIN_QUERY_LENGTH`:
   *  - Enter, provided the rep has NOT arrow-navigated this query (tracked by
   *    `hasNavigated` below, reset on every query change / dropdown reopen).
   *    Arrow-navigating first and then hitting Enter keeps the existing
   *    select-highlighted-item behavior — this prop never overrides that.
   *  - Clicking (or arrow-highlighting + Enter on) the dropdown's final "See
   *    all results" row, appended only when this prop is provided.
   */
  onSubmitQuery?: (q: string) => void;
  /**
   * D19 — explicit opt-in for a narrow-column mount (the enroll-queue
   * sidebar's ~476px dock). The `mode="all"` branch defaults to a fixed
   * `w-80` because that is right for its OTHER `mode="all"` mount (Command
   * Center's top-bar omnibox, `mini-apps/command-center/index.tsx`) — this
   * prop widens just the narrow-column caller without touching that
   * default. `contactsOnly`/`accountsOnly` mounts already render full-width
   * on their own and are unaffected either way.
   */
  fullWidth?: boolean;
  /**
   * Command Center's `?q=` seam (short entity-entry routes' search fallback,
   * `EntityEntryRoute`/`ContactRedirect` in
   * `web/app-home/src/components/flow/entity-routes.tsx`, plan D5). Seeds the
   * query text and opens the dropdown on mount, AND re-seeds/reopens on every
   * subsequent render where this prop's VALUE changes to a new truthy string
   * (render-time sync, not a controlled value — see the `prevInitialQuery`
   * block in the component body). This matters because `GlobalSearch` lives
   * in Command Center's persistent header and does NOT remount across the
   * app's internal route changes — a naive mount-only seed would miss a
   * fresh `?q=` arriving later (e.g. Back navigation restoring `?q=acme`
   * after a result select had cleared it). Omitted by every mount that
   * doesn't read `?q=` (contactsOnly/accountsOnly embeds) — behavior there is
   * unaffected.
   */
  initialQuery?: string;
}

function useFlowSearch(q: string, scope: FlowSearchScope, enabled: boolean) {
  const { apiFetch } = useApi();
  return useQuery<FlowSearchResponse>({
    queryKey: ['flow-search', q, scope],
    enabled: enabled && q.length >= MIN_QUERY_LENGTH,
    staleTime: Infinity,
    // Keep the previous dropdown contents while the next keystroke's query
    // runs — no flash-to-empty mid-typing.
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const qs = new URLSearchParams({ q, scope });
      return fetchJson(apiFetch, FlowSearchResponseSchema, `/api/flow/search?${qs.toString()}`, { timeoutMs: 30_000 });
    },
  });
}

type SearchItem =
  | { kind: 'account'; row: AccountSearchResult }
  | { kind: 'contact'; row: ContactSearchResult }
  | { kind: 'escalate' }
  | { kind: 'submit' }
  // Trigger 2 (D12/U5) — the /api/flow/lookup exact-match row. Rendered
  // regardless of `mode` (D8): the mount stays "contacts", so this is the
  // only account result contactsOnly hosts ever see.
  | { kind: 'identifier-account'; accountId: string; name: string | null; resolveTriggered: boolean };

export function GlobalSearch({ activeEntity, onSelectAccount, onSelectContact, mode = 'all', recentContacts, onSubmitQuery, fullWidth = false, initialQuery }: GlobalSearchProps) {
  const contactsOnly = mode === 'contacts';
  const accountsOnly = mode === 'accounts';
  // D19 — `fullWidth` widens the `mode="all"` branch only for callers that
  // opt in; `contactsOnly`/`accountsOnly` already force full-width above.
  const forceFullWidth = contactsOnly || accountsOnly || fullWidth;
  // `initialQuery` is seeded on mount via the useState initializer below, AND
  // re-synced whenever the prop value itself changes to a new truthy value
  // (see the render-time sync block right after these declarations, and this
  // prop's own doc comment on GlobalSearchProps for why a render-time sync —
  // not an effect — is needed here: GlobalSearch does NOT remount across
  // Command Center's internal route changes).
  const [q, setQ] = useState(initialQuery ?? '');
  const [scope, setScope] = useState<FlowSearchScope>('my');
  const [open, setOpen] = useState(!!initialQuery);
  const [highlight, setHighlight] = useState(0);
  // Search Results surface — "has the rep explicitly arrow-navigated THIS
  // query" (see onSubmitQuery's doc comment). Reset wherever the dropdown's
  // contents effectively start over: a query edit or a reopen; set true only
  // by an actual ArrowDown/ArrowUp move.
  const [hasNavigated, setHasNavigated] = useState(false);

  // Re-seed on a NEW truthy `initialQuery` (Codex adversarial-review fix,
  // 2026-09-11) — render-time "adjusting state when a prop changes" pattern
  // (React docs; same trick FlowTable.tsx's `prevGroupBy` and
  // ContactFlowTable.tsx's `prevContactQueryParam` use), not an effect,
  // since this needs to win the same render as the prop change, before
  // anything reads the stale `q`/`open`. Fires ONLY when `initialQuery`
  // itself changes to something new and truthy — never on every render
  // (the prop is a constant reference while the rep is just typing), and
  // never on a change to `undefined` (that case is `reset()`'s job, already
  // handled below — re-seeding here too would fight a rep who has since
  // cleared the box). GlobalSearch never unmounts across Command Center's
  // internal route changes (it lives in the persistent header above the
  // nested `<Routes>`), so without this, landing on `?q=acme` → selecting a
  // result (which clears `?q=` and this component's local state via
  // `reset()`) → pressing Back (which restores `?q=acme` to the URL and
  // recomputes this prop back to `"acme"`) left the search box empty/closed
  // instead of restoring — the component was still mounted, so the
  // mount-only `useState` initializers never re-ran.
  const [prevInitialQuery, setPrevInitialQuery] = useState(initialQuery);
  if (initialQuery !== prevInitialQuery) {
    setPrevInitialQuery(initialQuery);
    if (initialQuery) {
      setQ(initialQuery);
      setOpen(true);
      setHighlight(0);
      setHasNavigated(false);
    }
  }

  const debouncedQ = useDebouncedValue(q.trim(), GLOBAL_SEARCH_DEBOUNCE_MS);
  // Trigger 2 (D12/U5): identifier-shaped input routes to /api/flow/lookup
  // instead of fuzzy /api/flow/search — detection is MODE-INDEPENDENT (D8),
  // computed once here off the raw debounced text, never gated by
  // `contactsOnly`. Below the 2-char minimum there's nothing to classify yet.
  const isIdentifierQuery = debouncedQ.length >= MIN_QUERY_LENGTH
    && parseSearchIdentifier(debouncedQ).kind !== null;
  const query = useFlowSearch(debouncedQ, scope, !isIdentifierQuery);
  const lookup = useFlowLookup(debouncedQ, isIdentifierQuery);
  const ref = useClickOutside(open, () => setOpen(false));

  const data = !isIdentifierQuery && debouncedQ.length >= MIN_QUERY_LENGTH ? query.data : undefined;
  const isLoadingActive = isIdentifierQuery ? lookup.isLoading : query.isLoading;
  const hasError = isIdentifierQuery ? !!lookup.error : !!query.error;

  // R6: an empty query with recents available shows the "Recent" section on
  // focus instead of the usual "type to search" dropdown state — a separate
  // branch from the query-result sections below, no network involved.
  const showingRecents = q.trim().length === 0 && !!recentContacts?.length;

  // Flattened [preferred section, other section, escalation] for keyboard nav.
  const { sections, items } = useMemo(() => {
    if (showingRecents) {
      const rows: SearchItem[] = (recentContacts ?? []).map(row => ({ kind: 'contact' as const, row }));
      return { sections: [{ label: 'Recent', rows }], items: rows };
    }
    if (isIdentifierQuery) {
      // A single exact-match row (or nothing yet/no-match) — never the fuzzy
      // accounts/contacts sections, and never gated by `contactsOnly` (D8).
      //
      // Guard (bug fix): `useFlowLookup` fails closed on a malformed payload
      // (it surfaces as `hasError`, not data), so this only catches a
      // schema-valid but empty `account_id` (`''`), which would navigate to
      // `/accounts/` on select. Require BOTH `kind === 'account'` AND a
      // truthy `account_id` before treating the response as a selectable row.
      if (!lookup.data || lookup.data.kind !== 'account' || !lookup.data.account_id) {
        return { sections: [], items: [] };
      }
      const row = lookup.data;
      const item: SearchItem = {
        kind: 'identifier-account',
        accountId: row.account_id,
        name: row.name ?? null,
        resolveTriggered: row.resolve_triggered,
      };
      return { sections: [{ label: 'Account', rows: [item] }], items: [item] };
    }
    const accounts = contactsOnly
      ? []
      : (data?.accounts ?? []).slice(0, accountsOnly || activeEntity === 'account' ? PREFERRED_SLOTS : OTHER_SLOTS);
    const contacts = accountsOnly
      ? []
      : (data?.contacts ?? []).slice(
          0,
          contactsOnly || activeEntity === 'contact' ? PREFERRED_SLOTS : OTHER_SLOTS,
        );
    const accountItems: SearchItem[] = accounts.map(row => ({ kind: 'account' as const, row }));
    const contactItems: SearchItem[] = contacts.map(row => ({ kind: 'contact' as const, row }));
    const ordered = accountsOnly
      ? [{ label: 'Accounts', rows: accountItems }, { label: 'Contacts', rows: contactItems }]
      : contactsOnly || activeEntity === 'contact'
      ? [{ label: 'Contacts', rows: contactItems }, { label: 'Accounts', rows: accountItems }]
      : [{ label: 'Accounts', rows: accountItems }, { label: 'Contacts', rows: contactItems }];
    const flat: SearchItem[] = [...ordered[0].rows, ...ordered[1].rows, { kind: 'escalate' as const }];
    // Search Results surface: a final, keyboard-reachable "See all results"
    // row — additive, only when a host actually wants the escalation.
    if (onSubmitQuery && q.trim().length >= MIN_QUERY_LENGTH) {
      flat.push({ kind: 'submit' as const });
    }
    return { sections: ordered, items: flat };
  }, [data, activeEntity, contactsOnly, accountsOnly, showingRecents, recentContacts, onSubmitQuery, q, isIdentifierQuery, lookup.data]);

  // Highlight is reset via handlers (not an effect); clamp covers list shrink.
  const activeIndex = Math.min(highlight, items.length - 1);

  const close = () => setOpen(false);

  const reset = () => {
    setQ('');
    setScope('my');   // a fresh search starts scoped to my book
    setHighlight(0);
    setHasNavigated(false);
    setOpen(false);
  };

  const select = (item: SearchItem) => {
    if (item.kind === 'account' || item.kind === 'identifier-account') {
      // The identifier-account row navigates on select whether or not it's
      // mirrored yet (D12): an unmirrored hit has already fired the shared
      // force-fetch guard server-side, and /accounts/<id> (U4) owns the
      // resolving → synced polling once we land there.
      onSelectAccount(item.kind === 'account' ? item.row.id : item.accountId);
      reset();
    } else if (item.kind === 'contact') {
      onSelectContact?.(item.row.id, item.row.account_id, item.row);
      reset();
    } else if (item.kind === 'escalate') {
      setScope(s => (s === 'my' ? 'all' : 'my'));
      setHighlight(0);
    } else {
      // 'submit' — "See all results for {q}". Deliberately does NOT reset()
      // the query text: the escalation surface the host opens is keyed off
      // this exact query, and the omnibox stays populated above it.
      onSubmitQuery?.(q.trim());
      setOpen(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    // Reopen on arrow keys whenever there is anything to show — including the
    // empty-query Recents list (R6), which would otherwise be unreachable from
    // the keyboard after an Escape (recents only reappear on focus/click).
    // The reopening arrow IS a navigation step: land on a definite item (first
    // for ↓, last for ↑) and mark hasNavigated so Enter SELECTS the visibly-
    // highlighted row rather than escalating to the full results surface — the
    // highlight and Enter must agree (Spencer's QA, 2026-07-29).
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && (q.trim().length > 0 || items.length > 0)) {
      e.preventDefault();
      setOpen(true);
      setHasNavigated(true);
      setHighlight(e.key === 'ArrowDown' ? 0 : Math.max(items.length - 1, 0));
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, items.length - 1));
      setHasNavigated(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
      setHasNavigated(true);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = q.trim();
      // Enter with NO explicit arrow-navigation this query is a search
      // confirmation, never a silent select — `highlight` defaults to index
      // 0, so falling through to `select(items[activeIndex])` here would
      // jump the rep to the top-ranked fuzzy match just because they hit
      // Enter (GTMOPS bug: Command Center has no `onSubmitQuery`, so this
      // used to ALWAYS fall through). Escalate to the host's full results
      // surface if one is wired; otherwise no-op. Once the rep has actually
      // navigated, Enter reverts to the pre-existing select-highlighted-item
      // behavior below (Spencer's QA, 2026-07-29 — "the highlight and Enter
      // must agree").
      if (!hasNavigated) {
        // Trigger 2 (D12/U5): an identifier-shaped query resolves to at most
        // ONE exact-match row — there's no top-ranked-fuzzy-match ambiguity
        // to guard against (that's what the silent-select bug above was
        // about), so Enter selects it directly without requiring an
        // explicit arrow-navigation first.
        if (isIdentifierQuery) {
          const item = items[0];
          if (item) select(item);
          return;
        }
        // GTMOPS-2254 QA follow-up: when the query has NO matches in 'my'
        // book, the "Search all records" escalate row is the only actionable
        // item in the dropdown — Enter should trigger it exactly like a
        // click would, regardless of whether a host wired `onSubmitQuery`.
        // There's no fuzzy-match ambiguity to guard against here (there are
        // no matches), so this doesn't reopen the silent-select bug above.
        if (isEmpty && scope === 'my') {
          select({ kind: 'escalate' });
          return;
        }
        if (onSubmitQuery && trimmed.length >= MIN_QUERY_LENGTH) {
          onSubmitQuery(trimmed);
          setOpen(false);
        }
        return;
      }
      // The search is still in flight (debounce settled but the fetch
      // hasn't resolved yet) — `items` only contains the escalate row in
      // this window (the `data` memo below falls back to empty sections
      // until the query resolves), so selecting `items[activeIndex]` here
      // would misfire "Search all records" instead of acting on a real
      // result. No-op instead; the next Enter (once results land) acts on
      // the correct highlighted row. Does not affect the tooShort branch
      // (query disabled entirely, no fetch in flight) or the loaded/empty
      // branches (query.isLoading is false there).
      if (!showingRecents && !tooShort && query.isLoading) {
        return;
      }
      const item = items[activeIndex];
      if (item) select(item);
    }
  };

  const showDropdown = open && (q.trim().length > 0 || showingRecents);
  const tooShort = q.trim().length < MIN_QUERY_LENGTH;
  const isEmpty = isIdentifierQuery
    // `kind === 'none'` (a genuine no-match) OR an empty `account_id` that
    // fails the same guard the items memo above applies — both render the
    // same "No match found." state rather than a blank dropdown. A malformed
    // payload never gets here: it is `hasError` ("Search failed — try again.").
    ? !showingRecents && !tooShort && !isLoadingActive && !hasError
      && !!lookup.data && !(lookup.data.kind === 'account' && !!lookup.data.account_id)
    : !showingRecents && !tooShort && !isLoadingActive && !!data
      && (contactsOnly || data.accounts.length === 0) && (accountsOnly || data.contacts.length === 0);

  // Index bookkeeping for the flattened highlight across the two sections.
  let flatIndex = -1;
  const escalateIndex = items.findIndex(i => i.kind === 'escalate');
  const submitIndex = items.findIndex(i => i.kind === 'submit');

  const rowClass = (idx: number) =>
    `flex items-center gap-2.5 w-full px-2.5 py-1.5 text-left cursor-pointer ${
      idx === activeIndex ? 'bg-bg-secondary dark:bg-nightshade-700' : ''
    }`;

  return (
    <div className="relative" ref={ref} role="combobox" aria-expanded={showDropdown} aria-haspopup="listbox">
      <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-content-tertiary dark:text-nightshade-500 pointer-events-none" />
      <input
        {...AUTOFILL_IGNORE_PROPS}
        value={q}
        onChange={e => { setQ(e.target.value); setHighlight(0); setHasNavigated(false); setOpen(true); }}
        onFocus={() => { if (q.trim().length > 0 || showingRecents) { setOpen(true); setHasNavigated(false); } }}
        onKeyDown={onKeyDown}
        placeholder={contactsOnly ? 'Search contacts…' : accountsOnly ? 'Search accounts…' : 'Search people & companies…'}
        aria-label={contactsOnly ? 'Search contacts' : accountsOnly ? 'Search accounts' : 'Global search'}
        className={`pl-8 pr-3 py-1.5 text-[13px] border border-secondary dark:border-nightshade-700 rounded-md ${forceFullWidth ? 'w-full' : 'w-80'} bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100 placeholder:text-content-placeholder outline-none focus:border-content-action dark:focus:border-blueberry-500`}
      />
      {showDropdown && (
        <div
          role="listbox"
          className={`absolute left-0 top-full mt-1 z-30 ${forceFullWidth ? 'w-full' : 'w-[380px]'} bg-white dark:bg-nightshade-800 border border-secondary dark:border-nightshade-700 rounded-md shadow-lg overflow-hidden`}
        >
          {!showingRecents && tooShort && (
            <div className="px-2.5 py-2 text-[11.5px] text-content-tertiary dark:text-nightshade-500">
              Type at least {MIN_QUERY_LENGTH} characters…
            </div>
          )}
          {!showingRecents && !tooShort && isLoadingActive && (
            <div className="px-2.5 py-2 text-[11.5px] text-content-tertiary dark:text-nightshade-500">Searching…</div>
          )}
          {!showingRecents && !tooShort && hasError && (
            <div className="px-2.5 py-2 text-[11.5px] text-content-danger dark:text-red-400">Search failed — try again.</div>
          )}
          {isEmpty && (
            <div className="px-2.5 py-2 text-[11.5px] text-content-tertiary dark:text-nightshade-500">
              {isIdentifierQuery ? 'No match found.' : `No matches${scope === 'my' ? ' in your book' : ''}.`}
            </div>
          )}
          {/* Discoverable fetch hint (Spencer's QA, 2026-08-20): a genuine
              zero-result fuzzy search against the WHOLE mirror (scope='all')
              means the account may exist in SFDC but not Terra's mirror yet —
              the force-fetch path exists for exactly this, but only an
              identifier-shaped query (id or record URL) reaches it, so a
              plain-name miss is otherwise a dead end. Gated to scope='all'
              (not 'my'): escalating from 'my' to 'all' can still find the
              account elsewhere in the mirror, so suggesting a fetch before
              that escalation runs would be premature. Not shown for domain
              queries — a bare domain only searches `email_domain_link`
              (lookupFlowAccountByDomain) and never force-fetches, so
              pointing reps at it here would be its own dead end. */}
          {isEmpty && !isIdentifierQuery && scope === 'all' && (
            <div className="px-2.5 py-2 text-[11.5px] text-content-tertiary dark:text-nightshade-500 border-t border-secondary dark:border-nightshade-700">
              Not in Terra yet? Paste the Salesforce record link or account ID to fetch it.
            </div>
          )}
          {(showingRecents || (!tooShort && !isLoadingActive)) && sections.map(section => {
            if (section.rows.length === 0) return null;
            return (
              <div key={section.label} className="py-1 border-b border-secondary/60 dark:border-nightshade-700/60 last:border-b-0">
                <div className="px-2.5 py-1 text-[10.5px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500">
                  {section.label}
                </div>
                {section.rows.map(item => {
                  flatIndex += 1;
                  const idx = flatIndex;
                  if (item.kind === 'account') {
                    const { row } = item;
                    // email_domain_link is an SFDC HYPERLINK() formula (an <a> string),
                    // so parse it to a clean hostname label — the row itself navigates,
                    // so no nested anchor. Falls back to industry.
                    const sub = parseDomainLink(row.email_domain_link)?.label ?? row.industry;
                    return (
                      <div
                        key={`account-${row.id}`}
                        role="option"
                        aria-selected={idx === activeIndex}
                        onMouseEnter={() => setHighlight(idx)}
                        onMouseDown={e => e.preventDefault() /* keep input focus */}
                        onClick={() => select(item)}
                        className={rowClass(idx)}
                      >
                        <span className="w-7 h-7 rounded-full shrink-0 bg-bg-secondary dark:bg-nightshade-700 flex items-center justify-center">
                          <Building2 className="w-3.5 h-3.5 text-content-secondary dark:text-nightshade-400" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[12.5px] font-medium text-content-primary dark:text-nightshade-100 truncate">
                            {row.name ?? '—'}
                          </span>
                          {sub && (
                            <span className="block text-[11px] text-content-tertiary dark:text-nightshade-500 truncate">{sub}</span>
                          )}
                        </span>
                      </div>
                    );
                  }
                  if (item.kind === 'identifier-account') {
                    // Trigger 2 (D12/U5) exact-match row — a "Fetching from
                    // Salesforce…" label while `resolve_triggered` (the
                    // account isn't mirrored yet; the shared force-fetch
                    // guard already fired server-side), the real name once
                    // mirrored. Still selectable either way (D12): the
                    // target account_id is known and /accounts/<id> (U4)
                    // owns settling the resolving state once we land there.
                    return (
                      <div
                        key={`identifier-account-${item.accountId}`}
                        role="option"
                        aria-selected={idx === activeIndex}
                        onMouseEnter={() => setHighlight(idx)}
                        onMouseDown={e => e.preventDefault() /* keep input focus */}
                        onClick={() => select(item)}
                        className={rowClass(idx)}
                      >
                        <span className="w-7 h-7 rounded-full shrink-0 bg-bg-secondary dark:bg-nightshade-700 flex items-center justify-center">
                          <Building2 className="w-3.5 h-3.5 text-content-secondary dark:text-nightshade-400" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[12.5px] font-medium text-content-primary dark:text-nightshade-100 truncate">
                            {item.resolveTriggered ? 'Fetching from Salesforce…' : (item.name ?? '—')}
                          </span>
                        </span>
                      </div>
                    );
                  }
                  if (item.kind !== 'contact') return null; // sections never hold 'escalate' or 'submit'
                  const { row } = item;
                  const displayName = row.name ?? row.email ?? '?';
                  const subParts = [row.title, row.account_name].filter(Boolean);
                  return (
                    <div
                      key={`contact-${row.id}`}
                      role="option"
                      aria-selected={idx === activeIndex}
                      onMouseEnter={() => setHighlight(idx)}
                      onMouseDown={e => e.preventDefault() /* keep input focus */}
                      onClick={() => select(item)}
                      className={rowClass(idx)}
                    >
                      <span className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[11px] font-semibold text-white ${avatarColor(displayName)}`}>
                        {getInitials(displayName)}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[12.5px] font-medium text-content-primary dark:text-nightshade-100 truncate">
                          {displayName}
                        </span>
                        {subParts.length > 0 && (
                          <span className="block text-[11px] text-content-tertiary dark:text-nightshade-500 truncate">
                            {subParts.join(' · ')}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {/* Escalation footer — pinned, keyboard-reachable as the last item.
              Not rendered for the recents branch: a recents list isn't a
              query result, so there's nothing to escalate to "all records".
              Also not rendered for an identifier-shaped query (Trigger 2):
              a precise lookup has no 'my book' vs 'all records' scope to
              escalate between. */}
          {!showingRecents && !tooShort && !isIdentifierQuery && (
            <div
              role="option"
              aria-selected={escalateIndex === activeIndex}
              onMouseEnter={() => setHighlight(escalateIndex)}
              onMouseDown={e => e.preventDefault()}
              onClick={() => select({ kind: 'escalate' })}
              className={`flex items-center gap-1.5 px-2.5 py-2 text-[11.5px] cursor-pointer border-t border-secondary dark:border-nightshade-700 text-content-action dark:text-blueberry-400 ${
                escalateIndex === activeIndex ? 'bg-bg-secondary dark:bg-nightshade-700' : ''
              }`}
            >
              {scope === 'my' ? (
                <>Search all records <ArrowRight className="w-3 h-3" /></>
              ) : (
                <span className="flex items-center gap-1.5 text-content-secondary dark:text-nightshade-400">
                  Showing all records ·<span className="text-content-action dark:text-blueberry-400">just my book</span>
                </span>
              )}
            </div>
          )}
          {/* Search Results surface — final, keyboard-reachable "See all
              results" row. Only present (submitIndex !== -1) when a host
              provided onSubmitQuery AND the query clears the 2-char minimum
              — see the items memo above. Not rendered for the recents
              branch, matching the escalation footer above it. */}
          {submitIndex !== -1 && (
            <div
              role="option"
              aria-selected={submitIndex === activeIndex}
              onMouseEnter={() => setHighlight(submitIndex)}
              onMouseDown={e => e.preventDefault()}
              onClick={() => select({ kind: 'submit' })}
              className={`px-2.5 py-2 text-[11.5px] cursor-pointer border-t border-secondary dark:border-nightshade-700 text-content-action dark:text-blueberry-400 truncate ${
                submitIndex === activeIndex ? 'bg-bg-secondary dark:bg-nightshade-700' : ''
              }`}
            >
              {`See all results for "${q.trim()}" ↵`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
